// components
import Button from '@components/Button/Button';
import OnboardingLayout from '@components/OnboardingLayout/OnboardingLayout';
import { faDesktopAlt } from '@fortawesome/pro-light-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'motion/react';
import type { FC } from 'react';
import type { User } from 'types';

import { OnboardingFlowContainerForMobile } from './OnboardingFlowContainers';
import styles from './OnboardingMessages.module.scss';

interface SignoutMessageProps {
  handleLoginAsNewUser(): Promise<void>;
  isSigningOut: boolean;
  goToDashboard(): void;
  user: User;
  title: string;
}

const MessageContainer: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return (
    <motion.div
      className={styles['message-container']}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1, transition: { duration: 0.5 } }}
    >
      {children}
    </motion.div>
  );
};

const MessageContent: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['message-content']}>{children}</div>;
};

const MessagePrimaryText: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <p className={styles['message-primary']}>{children}</p>;
};

const MessageSecondaryText: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <p className={styles['message-secondary']}>{children}</p>;
};

const MessageButtonsContainer: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['message-btns-container']}>{children}</div>;
};

const MessageButtonContainer: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['message-btn-container']}>{children}</div>;
};

const AlreadyHasAnAccountMessage: FC<React.PropsWithChildren<SignoutMessageProps>> = ({
  handleLoginAsNewUser,
  isSigningOut,
  goToDashboard,
  user,
  title,
}) => {
  return (
    <OnboardingLayout title={title}>
      <MessageContainer>
        <MessageContent>
          <MessagePrimaryText>
            You are currently logged in as <strong>{user.signInEmailAddress}</strong>
          </MessagePrimaryText>
          <MessageSecondaryText>
            Would you like to create a new account or continue to the dashboard with your current
            login?
          </MessageSecondaryText>
          <MessageButtonsContainer>
            <MessageButtonContainer>
              <Button variant="secondary" onClick={handleLoginAsNewUser} loading={isSigningOut}>
                Create new account
              </Button>
            </MessageButtonContainer>
            <MessageButtonContainer>
              <Button variant="primary" onClick={goToDashboard}>
                Go to dashboard
              </Button>
            </MessageButtonContainer>
          </MessageButtonsContainer>
        </MessageContent>
      </MessageContainer>
    </OnboardingLayout>
  );
};

const UserIsOnMobileForInstructionsMessage: FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <OnboardingFlowContainerForMobile>
      <OnboardingLayout title={'Set up your signature'}>
        <div className={styles['message-content-container']}>
          <div className={styles['message-box']}>
            <div className={styles['envelope-icon']}>
              <FontAwesomeIcon icon={faDesktopAlt} />
            </div>
            <h2>Continue on your desktop</h2>
            <p className={styles['instructions-message']}>
              Please open the instructions email on your personal desktop
            </p>
          </div>
        </div>
      </OnboardingLayout>
    </OnboardingFlowContainerForMobile>
  );
};

const ImagesUploadingErrorMessage: FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <OnboardingLayout title={'Set up your signature'}>
      <div className={styles['message-content-container']}>
        <div className={styles['message-box']}>
          <div className={styles['envelope-icon']}>
            <FontAwesomeIcon icon={faExclamationCircle} />
          </div>
          <h2>Oops! Some images are not uploaded correctly</h2>
          <p className={styles['instructions-message']}>
            Please reload the page and try again later!
          </p>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export {
  AlreadyHasAnAccountMessage,
  ImagesUploadingErrorMessage,
  UserIsOnMobileForInstructionsMessage,
};
